import { useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useLocation, useHistory } from 'react-router'
import { useAppState, useAppDispatch } from 'Context/AppContext/AppContext'
import { SurveyType, useUserSurveysQuery } from 'Generated/graphql-types'
import useConfigureActivePlans from 'Hooks/useConfigureActivePlans/useConfigureActivePlans'
import { useSnoozeCheckInSurvey } from 'Hooks/useSnoozeCheckInSurvey'
import { SET_REDIRECT_PATH } from 'TypeDefs/AppContext'

const useLoginRedirect = ({ isPublic }: { isPublic: boolean }) => {
  const { isLoading, isAuthenticated, loginWithRedirect } = useAuth0()
  const { pathname } = useLocation()

  useEffect(() => {
    if (!isLoading && !isAuthenticated && (!isPublic || pathname === '/')) {
      loginWithRedirect()
    }
  }, [loginWithRedirect, isLoading, isAuthenticated, isPublic, pathname])

  return {
    loading: isLoading,
  }
}

const useNoAccountRedirect = () => {
  const { isLoading, isAuthenticated } = useAuth0()
  const { nonprofitOrgConfig, funderOrgConfig } = useAppState()

  const history = useHistory()
  const { pathname } = useLocation()

  const loading =
    isLoading || nonprofitOrgConfig.loading || funderOrgConfig.loading

  const shouldRedirect =
    !loading &&
    isAuthenticated &&
    nonprofitOrgConfig.errored &&
    funderOrgConfig.errored &&
    pathname !== '/no-account-found'

  useEffect(() => {
    if (shouldRedirect) {
      history.push('/no-account-found')
    }
  }, [shouldRedirect, history])

  return {
    loading,
  }
}

const useHomePageRedirect = () => {
  const history = useHistory()
  const { pathname } = useLocation()
  const { isLoading, isAuthenticated } = useAuth0()
  const { isFunderUser, configIsLoading } = useConfigureActivePlans()

  const loading = isAuthenticated ? configIsLoading : isLoading

  useEffect(() => {
    if (!loading && isAuthenticated && pathname === '/') {
      if (isFunderUser) {
        history.push('/funder/grantees')
      } else {
        history.push('/home')
      }
    }
  }, [pathname, loading, history, isFunderUser, isAuthenticated])

  return {
    loading,
  }
}

const useWelcomeRedirect = () => {
  const history = useHistory()
  const { pathname } = useLocation()
  const dispatch = useAppDispatch()
  const { isLoading, isAuthenticated } = useAuth0()
  const {
    userConfig: { firstName, lastName, loading: appStateLoading },
  } = useAppState()
  const loading = isAuthenticated ? appStateLoading : isLoading

  useEffect(() => {
    if (
      !loading &&
      isAuthenticated &&
      (!firstName || !lastName) &&
      pathname !== '/welcome' &&
      pathname !== '/no-account-found'
    ) {
      dispatch({
        type: SET_REDIRECT_PATH,
        payload: {
          redirectPath: pathname,
        },
      })

      history.push('/welcome')
    }
  }, [
    dispatch,
    history,
    pathname,
    firstName,
    lastName,
    loading,
    isAuthenticated,
  ])

  return {
    loading,
  }
}

const useNeedsAssessmentRedirect = () => {
  const {
    userConfig: { createdAt, permissions, loading: userLoading },
    nonprofitOrgConfig: { orgId: nonprofitOrgId, loading: orgLoading },
  } = useAppState()
  const { pathname } = useLocation()
  const history = useHistory()
  const { isLoading, isAuthenticated } = useAuth0()
  const { data: { userSurveys } = {}, loading: surveyLoading } =
    useUserSurveysQuery({
      variables: { nonprofitOrgId, type: SurveyType.NeedsAssessment },
      skip: !nonprofitOrgId || !permissions.includes('page:get_started'),
    })

  const loading = isAuthenticated
    ? surveyLoading || userLoading || orgLoading
    : isLoading

  useEffect(() => {
    if (!loading && isAuthenticated && userSurveys !== undefined) {
      let hasCompletedUserSurvey = false
      let hasIncompleteUserSurvey = false

      userSurveys?.forEach((s) => {
        if (s.isCompleted) hasCompletedUserSurvey = true
        else hasIncompleteUserSurvey = true
      })

      const shouldSeeSurvey = hasIncompleteUserSurvey || !hasCompletedUserSurvey

      if (
        permissions.includes('page:get_started') &&
        shouldSeeSurvey &&
        pathname !== '/welcome' && // Allow going through welcome page first
        pathname !== '/get-started'
      ) {
        history.push('/get-started')
      }
    }
  }, [
    loading,
    createdAt,
    history,
    pathname,
    userSurveys,
    permissions,
    isAuthenticated,
  ])

  return {
    loading,
  }
}

export const useCheckInSurveyRedirect = () => {
  const {
    userConfig: { permissions, loading: userLoading },
    nonprofitOrgConfig: { loading: orgLoading },
  } = useAppState()
  const { pathname } = useLocation()
  const history = useHistory()
  const { isLoading, isAuthenticated } = useAuth0()

  const { loading: isCheckInSurveyLoading, isTimeForCheckInSurvey } =
    useSnoozeCheckInSurvey()

  const loading = isAuthenticated
    ? isCheckInSurveyLoading || userLoading || orgLoading
    : isLoading

  useEffect(() => {
    if (!loading && isAuthenticated) {
      if (
        isTimeForCheckInSurvey &&
        permissions.includes('page:get_started') &&
        pathname !== '/welcome' && // Allow going through welcome page first
        pathname !== '/get-started' && // Allow going through Needs Assessment first
        pathname !== '/check-in'
      )
        history.push('/check-in')
    }
  }, [
    history,
    isAuthenticated,
    isTimeForCheckInSurvey,
    loading,
    pathname,
    permissions,
  ])

  return {
    loading,
  }
}

/*
 * This hook handles all redirects in the app that need to be checked on every
 * page, not just one specific page.
 *
 * These redirects should be handled in separate hooks that return a loading
 * property which is true while the hook is waiting for any data it needs to
 * determine whether to redirect.
 */
export const useGlobalRedirects = ({ isPublic }: { isPublic: boolean }) => {
  // Redirect to the login page if unauthenticated
  const { loading: loginRedirectLoading } = useLoginRedirect({ isPublic })

  const { loading: noAccountRedirectLoading } = useNoAccountRedirect()

  // Redirect to the corresponding home page from `/`
  const { loading: homePageRedirectLoading } = useHomePageRedirect()

  // Redirect to the welcome page if missing a name
  const { loading: welcomeRedirectLoading } = useWelcomeRedirect()

  // Redirect to Needs Assessment if user has not completed one and meets the criteria
  const { loading: needsAssessmentLoading } = useNeedsAssessmentRedirect()

  // Redirect to the Check-In Survey if user has not completed one and meets the criteria
  const { loading: checkInSurveyLoading } = useCheckInSurveyRedirect()

  return {
    loading:
      loginRedirectLoading ||
      noAccountRedirectLoading ||
      homePageRedirectLoading ||
      welcomeRedirectLoading ||
      needsAssessmentLoading ||
      checkInSurveyLoading,
  }
}
